import { ServiceWorkerUpdateAlert } from 'components/core/ServiceWorkerUpdateAlert';
import React, { useEffect, useState } from 'react';
import { logError } from 'setup/logger/logError';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { ENVIRONMENTS } from 'shared/utils/environment/types';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { getEnvironment, isDevEnv } from 'shared/utils/environment/determineEnvironment';
import { shouldEnableServiceWorkerInDev } from 'shared/utils/environment/variables';
import { logInfo } from 'setup/logger/logInfo';
import { showToast } from 'services/toaster';
import { frontendVersionWithBuildNumber } from 'shared/version';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { isPublicDownloadRoute } from 'setup/navigation/isPublicDownloadRoute';
import { deleteAllCaches } from 'services/serviceWorker/clearServiceWorkers';
import { isServiceWorkerRegistrationBlocked } from 'services/serviceWorker/isServiceWorkerRegistrationBlocked';
import { watchDeployedVersion } from 'services/serviceWorker/deployedVersionWatch';

let isRegistrationErrorReported = false;

function shouldDisplayAlert(): boolean {
  if (!isDevEnv()) {
    return true;
  }
  
  return shouldEnableServiceWorkerInDev();
}

export const WithServiceWorkerStatusCheck: React.FC = () => {
  const environment = getEnvironment();

  const [error, setError] = useState<Error | null>(null);

  // A tab kept open across a deploy learns of it and offers the update banner (PT-7331).
  useEffect(() => watchDeployedVersion(), []);

  // Unregister old CRA service worker once
  useEffect(() => {
    // Check if we already cleaned up old SW
    if (LocalStorageManager.wasOldServiceWorkerCleaned()) {
      return;
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then(async (registrations) => {
          let foundOldSW = false;

          for (const registration of registrations) {
            const scriptURL =
              registration.active?.scriptURL ||
              registration.waiting?.scriptURL ||
              registration.installing?.scriptURL;

            // Check if this is the old CRA service worker
            if (scriptURL?.includes('/service-worker.js')) {
              foundOldSW = true;
              logInfo(
                `[CRA] Unregistering old CRA service worker: ${scriptURL}`
              );

              try {
                await registration.unregister();
                logInfo(
                  '[CRA] Old service worker successfully unregistered'
                );
              } catch (error) {
                logError(
                  '[CRA] Failed to unregister old service worker',
                  error
                );
              }
            }
          }

          if (foundOldSW) {
            // Clear all caches related to old service worker
            await deleteAllCaches((cacheName) => logInfo(`[CRA] Clearing cache: ${cacheName}`));

            // Mark as cleaned and reload page to start fresh
            LocalStorageManager.markOldServiceWorkerCleaned();
            logInfo('[CRA] Old SW cleanup complete, reloading page...');
            window.location.reload();
          } else {
            // No old SW found, mark as cleaned
            LocalStorageManager.markOldServiceWorkerCleaned();
          }
        });
    }
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (!registration) {
        logError(`No service worker registration object`);
        return null;
      }

      if (registration.active) {
        registration.active.addEventListener('statechange', (e) => {
          logInfo(
            `React component: Active SW state changed:
            ${registration.active?.state}`
          );
        });
      }
      const isActivated = registration.active?.state === 'activated';
      if (environment === ENVIRONMENTS.PRODUCTION) {
        return null;
      }
      if (isActivated) {
        showToast({
            type: TOASTER_TYPES.SUCCESS,
            message: 'Service Worker works correctly',
            toasterPosition: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            hideDelay: 2000,
          });
      } else {
        showToast({
            type: TOASTER_TYPES.FAILURE,
            message: `Service Worker status: ${isActivated ? 'Activated' : 'Not Activated'}`,
            toasterPosition: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            hideDelay: 5000,
          });
      }
    },
    onRegisterError(error) {
      // Registration is retried (update checks): one report per page load is enough.
      const isFirstReport = !isRegistrationErrorReported;
      isRegistrationErrorReported = true;
      if (isServiceWorkerRegistrationBlocked(error)) {
        // Not an error of the app (a script replaced `register`): marked in the logs, not reported
        // to Sentry, and no toast or reload, which could not fix it and would only loop.
        if (isFirstReport) {
          logInfo('Service worker registration blocked', { registrationBlocked: true, error });
        }
        return;
      }
      if (isFirstReport) {
        logError('Service worker registration error', error);
      }
      // Public download pages fall back to direct HTTP; a reload loop would only hide the result.
      if (isPublicDownloadRoute(window.location.pathname)) {
        return;
      }
      setError(error);
      showToast({
          type: TOASTER_TYPES.FAILURE,
          message: 'There are some problems with Service worker: ' + error,
          toasterPosition: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          hideDelay: 10000,
        });
    },
  });

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => {
        window.location.reload();
      }, 15000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  if (needRefresh && shouldDisplayAlert()) {
    return (
      <ServiceWorkerUpdateAlert
        onRefresh={async () => {
              const version = frontendVersionWithBuildNumber;
              logInfo(
                '🔄 Starting Service Worker update after refresh',
                {
                  environment,
                  frontendVersion: version,
                },
                true
              );
              
              try {
                await updateServiceWorker(true);
                
                await new Promise<void>((resolve) => {
                  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                    navigator.serviceWorker.addEventListener(
                      'controllerchange',
                      () => {
                        logInfo('✅ New Service Worker took control');
                        resolve();
                      },
                      { once: true }
                    );
                    setTimeout(resolve, 1000);
                  } else {
                    resolve();
                  }
                });
                
                logInfo(
                  '✅ Service Worker successfully updated',
                  {
                    environment,
                    frontendVersion: version,
                  },
                  true
                );
                
                window.location.reload();
              } catch (error) {
                logError(
                  '❌ Error updating Service Worker',
                  {
                    error,
                    environment,
                    frontendVersion: version,
                  },
                  true
                );
              }
        }}
      />
    );
  }
};
